import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";
import { MatSnackBar } from "@angular/material";
import { Router } from "@angular/router";
import { TranslocoService } from '@ngneat/transloco';
import { NbGlobalPhysicalPosition, NbToastrService } from "@nebular/theme";

import { AuthService } from "../../shared/services/auth.service";
import { ParcAccessService } from "./../../shared/services/parc-access.service";
import { errorCodes } from "./../../shared/constants/constants";
import { MyErrorStateMatcher } from "../../shared/app-classes";
import { translate } from "@ngneat/transloco";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('emailInput') emailInput: ElementRef;
  @ViewChild('passwordInput') passwordInput: ElementRef;

  loginForm: FormGroup;
  matcher = new MyErrorStateMatcher();
  subUser = new Subscription();
  user: IUser;

  lang = '';
  placeholderPassword = '';
  placeholderLoginButton = '';
  placeholderForgotPassword = '';
  emailValidy = '';
  resetLink = '';
  ErrorDuringPasswordReset = '';
  showLoginSpinner = false;
  showSpinner = false;
  hidePassword = true;
  isResetting = false;
  verificationCode = '';
  isVerifying = false;

  // ✅ After 3 failed attempts (or an expired code) the user must request a new code.
  mustRequestNewCode = false;
  mustRequestNewCodeMsg = '';

  // ✅ True for the whole duration of login(): stops the auth.user subscription
  // from calling goToDefault() (and skipping the 2FA popup) on the session that
  // login() itself just created.
  private loginInProgress = false;

  // ✅ 2FA UI STATE - plain in-memory fields.
  // Not persisted: a fresh load of /login (refresh, logout redirect, abandoned
  // flow) must NEVER show a stale modal. The only cross-reload signal we keep is
  // the sessionStorage flag '2fa_pending' (set while a challenge is open) so that
  // ngOnInit can sign out a user who is authenticated at Firebase but never
  // passed 2FA.
  show2FA = false;
  is2FARequired = false;
  userEmail = '';
  codeSentToAdmins = false;

  // ✅ TRANSLATED 2FA MESSAGES
  twoFATitle = '';
  twoFAText = '';
  twoFATextAdmin = '';
  twoFAPlaceholder = '';
  twoFAVerifyButton = '';
  twoFAResendButton = '';
  twoFAResendAdminButton = '';
  twoFAExpiryText = '';
  twoFAInvalidCode = '';
  twoFACodeSent = '';
  twoFACodeSentToAdmin = '';
  twoFAResendFailed = '';
  twoFATooManyAttempts = '';
  twoFACodeExpired = '';

  passwordLengthValid: boolean;
  lowercaseValid: boolean;
  digitValid: boolean;
  specialCharValid: boolean;
  affinityValid: boolean;
  strengthScore: number;

  rolesCache: any = null;

  constructor(
    private auth: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private pas: ParcAccessService,
    private toaster: NbToastrService,
    private translocoService: TranslocoService
  ) {
    this.loginForm = new FormGroup({
      email: new FormControl("", [Validators.required, Validators.email]),
      password: new FormControl("", [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.lang = sessionStorage.getItem('language') || 'fr';
    this.placeholderPassword = this.lang === 'fr' ? 'Mot de passe' : 'Password';
    this.placeholderLoginButton = this.lang === 'fr' ? 'Connexion' : 'Sign in';
    this.placeholderForgotPassword = this.lang === 'fr'
      ? 'Mot de passe oublié ?'
      : 'Forgot your password ?';
    this.emailValidy = this.lang === 'fr' ? "Veuillez saisir un email valide." : "Please enter a valid email.";
    this.resetLink = this.lang === 'fr' ? "Un lien de réinitialisation a été envoyé." : "A reset link has been sent.";
    this.ErrorDuringPasswordReset = this.lang === 'fr' ? "Erreur lors de la réinitialisation." : "Error during password reset.";

    // 2FA Translations
    this.twoFATitle = this.lang === 'fr' ? '🔐 Authentification à deux facteurs' : '🔐 Two-Factor Authentication';
    this.twoFAText = this.lang === 'fr' 
      ? 'Nous avons envoyé un code de vérification à' 
      : 'We\'ve sent a verification code to';
    this.twoFATextAdmin = this.lang === 'fr'
      ? 'Un code a été envoyé à votre administrateur. Contactez-le pour obtenir le code.'
      : 'A code was sent to your administrator. Contact them to get the code.';
    this.twoFAPlaceholder = this.lang === 'fr' ? 'Entrez le code à 6 chiffres' : 'Enter 6-digit code';
    this.twoFAVerifyButton = this.lang === 'fr' ? 'Vérifier' : 'Verify';
    this.twoFAResendButton = this.lang === 'fr' ? 'Renvoyer le code' : 'Resend Code';
    this.twoFAResendAdminButton = this.lang === 'fr' ? 'Renvoyer le code à l\'administrateur' : 'Resend Code to administrator';
    this.twoFAExpiryText = this.lang === 'fr' ? 'Le code expire dans 1 heure' : 'Code expires in 1 hour';
    this.twoFAInvalidCode = this.lang === 'fr' 
      ? 'Veuillez entrer un code valide à 6 chiffres' 
      : 'Please enter a valid 6-digit code';
    this.twoFACodeSent = this.lang === 'fr' 
      ? 'Nouveau code envoyé à votre email !' 
      : 'New code sent to your email!';
    this.twoFACodeSentToAdmin = this.lang === 'fr'
      ? 'Nouveau code envoyé à votre administrateur !'
      : 'New code sent to your administrator!';
    this.twoFAResendFailed = this.lang === 'fr'
      ? 'Échec de l\'envoi du code. Veuillez réessayer.'
      : 'Failed to resend code. Please try again.';
    this.twoFATooManyAttempts = this.lang === 'fr'
      ? 'Vous avez saisi 3 codes incorrects. Veuillez demander un nouveau code.'
      : 'You have entered 3 incorrect codes. Please request a new code.';
    this.twoFACodeExpired = this.lang === 'fr'
      ? 'Ce code a expiré. Veuillez demander un nouveau code.'
      : 'This code has expired. Please request a new code.';

    // ✅ Clean slate on every load of the login page: a previous 2FA challenge
    // that was interrupted (refresh, back button, logout) may have left the user
    // authenticated at Firebase without passing 2FA. Sign them out and make sure
    // no stale modal is shown.
    const twoFAWasPending = sessionStorage.getItem('2fa_pending') === 'true';
    sessionStorage.removeItem('2fa_pending');
    // legacy keys from the previous sessionStorage-backed implementation
    sessionStorage.removeItem('2fa_show');
    sessionStorage.removeItem('2fa_required');
    sessionStorage.removeItem('2fa_email');
    sessionStorage.removeItem('2fa_refresh');
    this.show2FA = false;
    this.is2FARequired = false;
    this.mustRequestNewCode = false;
    this.mustRequestNewCodeMsg = '';
    this.verificationCode = '';
    if (twoFAWasPending) {
      this.auth.doSignout();
    }

    // ✅ Handle browser back button during an open 2FA challenge
    window.addEventListener('popstate', () => {
      if (this.is2FARequired) {
        this.close2FA();
      }
    });

    // ✅ SUBSCRIPTION WITH 2FA BLOCK
    this.subUser = this.auth.user.subscribe(async (u) => {
      if (u) {
        // Always keep this.user (Firestore user doc) fresh - goToDefault() needs it.
        this.user = u;
        // But only auto-navigate when we are NOT: tearing down an interrupted
        // 2FA flow, in an open challenge, or in the middle of login().
        if (!twoFAWasPending && !this.is2FARequired && !this.loginInProgress) {
          await this.goToDefault();
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Autofill detection
    setTimeout(() => {
      const email = this.emailInput.nativeElement.value;
      const password = this.passwordInput.nativeElement.value;

      if (email && password) {
        this.loginForm.controls['email'].setValue(email);
        this.loginForm.controls['password'].setValue(password);

        const valid = this.checkPasswordStrength(email, password);
        if (valid) this.login();
      }
    }, 500);
  }

  ngOnDestroy(): void {
    this.subUser.unsubscribe();
  }

  checkPasswordStrength(password: string, email: string): boolean {
    const lowerPassword = password.toLowerCase();

    this.passwordLengthValid = password.length >= 12;
    this.lowercaseValid = /[a-z]/.test(password);
    this.digitValid = /\d/.test(password);
    this.specialCharValid = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    let score = 0;
    if (this.lowercaseValid) score++;
    if (this.digitValid) score++;
    if (this.specialCharValid) score++;
    this.strengthScore = score;

    this.affinityValid = true;
    if (email) {
      const emailParts = email.toLowerCase().split(/[@.]/);
      for (let part of emailParts) {
        if (part.length >= 3 && lowerPassword.indexOf(part) !== -1) {
          this.affinityValid = false;
          break;
        }
      }
    }

    return (
      this.passwordLengthValid &&
      this.lowercaseValid &&
      this.digitValid &&
      this.specialCharValid &&
      this.affinityValid
    );
  }

  /**
   * Close 2FA only if clicking directly on the overlay (outside the modal)
   */
  close2FAOnOutsideClick(event: MouseEvent): void {
    // ✅ Only close if the click target is the overlay itself, not a child
    if (event.target === event.currentTarget) {
      this.close2FA();
    }
  }

  /**
   * Close 2FA popup and logout (for security)
   */
  close2FA(): void {
    this.is2FARequired = false;
    this.show2FA = false;
    this.isVerifying = false;
    this.verificationCode = '';
    this.mustRequestNewCode = false;
    this.mustRequestNewCodeMsg = '';
    this.codeSentToAdmins = false;
    sessionStorage.removeItem('2fa_pending');

    this.auth.doSignout().then(() => {
      this.router.navigate(['/login']);
    });
  }

  private showToast2FA(message: string): void {
    this.toaster.show(message, '', {
      duration: 3000,
      hasIcon: false,
      position: NbGlobalPhysicalPosition.BOTTOM_LEFT
    });
  }

  private twoFAWrongCode(remaining: number): string {
    if (this.lang === 'fr') {
      return `Code incorrect. Il vous reste ${remaining} tentative${remaining > 1 ? 's' : ''}.`;
    }
    return `Incorrect code. You have ${remaining} attempt${remaining > 1 ? 's' : ''} left.`;
  }

  async login(): Promise<void> {
    if (!this.loginForm.valid) return;

    this.loginInProgress = true;
    this.showLoginSpinner = true;
    this.isResetting = true;

    try {
      await this.auth.doEmailLogin(
        this.loginForm.controls["email"].value,
        this.loginForm.controls["password"].value
      );

      const valid = this.checkPasswordStrength(
        this.loginForm.controls["password"].value,
        this.loginForm.controls["email"].value
      );

      if (!valid) {
        const email = this.loginForm.controls["email"].value;
        this.auth.sentResetLink(email, "depot")
          .then(link => setTimeout(() => window.location.href = link, 300))
          .catch(err => console.error("Could not send reset link", err))
          .finally(() => this.showLoginSpinner = false);

        return;
      }
      const email = this.loginForm.controls["email"].value;
      // ✅ 3. Check if 2FA is needed (based on the trusted-device token)
      this.userEmail = email;

      const needs2FA = await this.auth.needs2FA(email);

      if (!needs2FA) {
        // This device was verified less than 6 months ago - skip 2FA completely
        this.is2FARequired = false;
        this.show2FA = false;
        this.showLoginSpinner = false;
        this.isResetting = false;
        await this.goToDefault();
        return;
      }

      // ✅ 4. User needs 2FA - send code and show popup
      this.is2FARequired = true;
      // Mark the challenge as open so an interrupted flow (refresh) gets
      // signed out on the next load of /login.
      sessionStorage.setItem('2fa_pending', 'true');

      // Send 2FA code
      const response = await this.auth.send2FACode(email);

      // Check if already verified (shouldn't happen if needs2FA worked)
      if (response && response.alreadyVerified) {
        this.is2FARequired = false;
        this.show2FA = false;
        sessionStorage.removeItem('2fa_pending');
        this.showLoginSpinner = false;
        this.isResetting = false;
        await this.goToDefault();
        return;
      }

      // ✅ SHOW THE POPUP
      this.codeSentToAdmins = !!(response && response.sentToAdmins);
      this.mustRequestNewCode = false;
      this.mustRequestNewCodeMsg = '';
      this.verificationCode = '';
      this.show2FA = true;
      this.showLoginSpinner = false;
      this.isResetting = false;

    } catch (error) {
      // A 2FA infrastructure failure must not leave a half-open challenge and
      // must fail closed: if the password step already succeeded the user is
      // authenticated at Firebase, so sign them back out.
      this.is2FARequired = false;
      this.show2FA = false;
      sessionStorage.removeItem('2fa_pending');
      this.auth.doSignout().catch(() => { });

      let message = errorCodes[error.code] || translate("Echec authentification");

      this.toaster.show(message, "", {
        duration: 2000,
        hasIcon: false,
        position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
      });

      this.showLoginSpinner = false;
      this.isResetting = false;
    } finally {
      this.loginInProgress = false;
    }
  }

  async verify2FA(): Promise<void> {
    // Blocked until a fresh code is requested (3 failed tries / expired code).
    if (this.mustRequestNewCode) {
      this.toaster.show(this.mustRequestNewCodeMsg, '', {
        duration: 3000,
        hasIcon: false,
        position: NbGlobalPhysicalPosition.BOTTOM_LEFT
      });
      return;
    }

    if (!this.verificationCode || this.verificationCode.length !== 6) {
      this.toaster.show(this.twoFAInvalidCode, '', {
        duration: 3000,
        hasIcon: false,
        position: NbGlobalPhysicalPosition.BOTTOM_LEFT
      });
      return;
    }

    this.isVerifying = true;

    try {
      const isValid = await this.auth.verify2FACode(
        this.userEmail,
        this.verificationCode
      );

      if (isValid) {
        // ✅ CLEAR 2FA STATE
        this.is2FARequired = false;
        this.show2FA = false;
        this.isVerifying = false;
        this.verificationCode = '';
        this.mustRequestNewCode = false;
        this.mustRequestNewCodeMsg = '';
        this.codeSentToAdmins = false;
        sessionStorage.removeItem('2fa_pending');

        await this.goToDefault();
      }
    } catch (error) {
      const remaining = (error && typeof error.attemptsRemaining === 'number')
        ? error.attemptsRemaining
        : null;

      if (error && (error.code === 'TOO_MANY_ATTEMPTS' || remaining === 0)) {
        // 3 failed attempts (or lockout): switch the popup into "request a new code" mode.
        this.mustRequestNewCode = true;
        this.mustRequestNewCodeMsg = this.twoFATooManyAttempts;
        this.showToast2FA(this.mustRequestNewCodeMsg);
      } else if (error && (error.code === 'EXPIRED' || error.code === 'NO_CODE')) {
        this.mustRequestNewCode = true;
        this.mustRequestNewCodeMsg = this.twoFACodeExpired;
        this.showToast2FA(this.mustRequestNewCodeMsg);
      } else if (remaining !== null && remaining > 0) {
        // Wrong code, attempts left: tell the user how many remain.
        this.showToast2FA(this.twoFAWrongCode(remaining));
      } else {
        this.showToast2FA((error && error.message) || this.twoFAInvalidCode);
      }

      this.isVerifying = false;
      this.verificationCode = '';
    }
  }

  async resend2FA(): Promise<void> {
    if (!this.userEmail) {
      // No active challenge - nothing to resend.
      this.close2FA();
      return;
    }
    try {
      const result = await this.auth.send2FACode(this.userEmail);
      // Fresh code issued: unblock the popup.
      this.mustRequestNewCode = false;
      this.mustRequestNewCodeMsg = '';
      this.verificationCode = '';
      this.codeSentToAdmins = !!(result && result.sentToAdmins);
      this.showToast2FA(this.codeSentToAdmins ? this.twoFACodeSentToAdmin : this.twoFACodeSent);
    } catch (error) {
      // Backend already returns a localized message (cooldown / rate-limit / etc.).
      this.showToast2FA((error && error.message) || this.twoFAResendFailed);
    }
  }

sendPasswordReset() {
  if (this.isResetting) return;

  const emailCtrl = this.loginForm.controls['email'];
  const email = emailCtrl.value.toLowerCase().trim();

  if (!email || !emailCtrl.valid) {
    this.showToast(this.emailValidy, 3000);
    return;
  }

  this.isResetting = true;
  this.showSpinner = true;

  this.auth.SendResetPassword("Customer", email, "depot")
    .then(() => {
      this.showToast(this.resetLink, 4000);
    })
    .catch((error) => {
      console.error("error", error);

      const message = this.extractErrorMessage(error);
      this.showToast(this.translocoService.translate(message), 4000);
    })
    .finally(() => {
      this.isResetting = false;
      this.showSpinner = false;
    });
}

// Méthode utilitaire pour afficher un toast
private showToast(message: string, duration = 3000) {
  this.toaster.show(message, '', {
    duration,
    hasIcon: false,
    position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
  });
}

// Méthode utilitaire pour extraire un message d'erreur clair
private extractErrorMessage(error: any): string {
  if (error.code && errorCodes[error.code]) return errorCodes[error.code];
  if (typeof error === 'string') return error;
  if (error.message) {
    try {
      const parsed = JSON.parse(error.message.replace(/^.*?:\s*/, ""));
      return parsed.error || error.message;
    } catch {
      return error.message;
    }
  }
  return this.ErrorDuringPasswordReset;
}


  async goToDefault() {
    // ✅ BLOCK if 2FA is required
    if (this.is2FARequired) {
      return;
    }
    
    // ✅ Reset flags after successful navigation
    this.is2FARequired = false;
    this.show2FA = false;
    this.isVerifying = false;
    this.verificationCode = '';
    this.mustRequestNewCode = false;
    this.mustRequestNewCodeMsg = '';
    this.codeSentToAdmins = false;
    this.showLoginSpinner = false;
    this.isResetting = false;
    sessionStorage.removeItem('2fa_pending');

    try {
      // login()'s non-2FA / already-verified paths call goToDefault() directly;
      // make sure the Firestore user doc is loaded even if the auth.user
      // subscription hasn't emitted it yet.
      if (!this.user) {
        this.user = await this.auth.user.pipe(filter(u => !!u), take(1)).toPromise();
      }

      if (!this.rolesCache) {
        this.rolesCache = await this.pas.getRolesOnce();
      }

      const rolesUser = this.user.roles;
      const roles = this.rolesCache;
      const success = this.lang === "fr" ? "Connexion réussie" : "Login successful";

      let isAdmin = false, isTrucker = false, isDepotUser = false;

      for (let role of rolesUser) {
        if (role === "admin") isAdmin = true;
        if (role === "truckerGate") isTrucker = true;
      }

      if (roles.DepotWeb) {
        for (let r of rolesUser) {
          if (roles.DepotWeb.includes(r) && r !== "truckerGate") {
            isDepotUser = true;
            break;
          }
        }
      }

      const isAuth = isAdmin || isTrucker || isDepotUser;

      if (isAuth) {
        if (isTrucker) this.router.navigate(["user/truckerHome"]);
        else if (isDepotUser) this.router.navigate(["user/dashboard"]);
        else if (isAdmin) {
          localStorage.removeItem("selectedParc");
          const allParcs = this.user["allParcs"];
          localStorage.setItem("selectedParc", allParcs[0]);
          this.router.navigate(["user/addNewYard"]);
        }

        this.toaster.show(success, "", {
          duration: 2000,
          hasIcon: false,
          position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
        });
      } else {
        const erreur = this.lang === "fr"
          ? "Echec authentification"
          : "Login failed: your user ID or password is incorrect";

        this.toaster.show(erreur, "", {
          duration: 2000,
          hasIcon: false,
          position: NbGlobalPhysicalPosition.BOTTOM_LEFT,
        });
        this.router.navigate(["/login"]);
      }

      this.showLoginSpinner = false;
      this.isResetting = false;

    } catch (e) {
      console.log("error here pls check 😁", e);
    }
  }

}
